import { zodResolver } from '@hookform/resolvers/zod';
import { Chip, FormControlLabel, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material';
import { isNotNullOrUndefined } from '@weft/utils';
import { get } from 'lodash';
import React from 'react';
import { useForm } from 'react-hook-form';
import VyDialog from '../../../../../components/new-design/feedback/VyDialog';
import { useDialog } from '../../../../../hooks/use-dialog';
import { useVyNavigate } from '../../../../../hooks/use-vy-navigate';
import type { CreateProductDialogFormInput } from './create-product-dialog-types/create-product-dialog-form-input-schema';
import type { NewCreateProductDialogForm } from './create-product-dialog-types/new-create-product-dialog-form-schema';
import { NewCreateProductDialogFormSchema } from './create-product-dialog-types/new-create-product-dialog-form-schema';

type CreateProductDialogProps = {
  children: React.ReactElement;
};

export const CreateProductDialog: React.FC<CreateProductDialogProps> = ({ children }) => {
  const navigate = useVyNavigate();
  const { isDialogOpen, handleOpen, handleClose } = useDialog();

  const { handleSubmit: formHandleSubmit, register, formState: { errors } } = useForm<CreateProductDialogFormInput, unknown, NewCreateProductDialogForm>({
    resolver: zodResolver(NewCreateProductDialogFormSchema),
  });

  const handleSubmit = formHandleSubmit((data) => {
    navigate('/products/create', {
      replace: true,
      state: data,
    });
  });

  const nameRegister = register('name');
  const descriptionRegister = register('description');

  return (
    <>
      {React.cloneElement(children, {
        onClick: handleOpen,
      })}

      <VyDialog
        component="form"
        onSubmit={handleSubmit}
        open={isDialogOpen}
        onClose={handleClose}
        label="Create new product"
        labelDescription="Set new metered or standard product"
        ctaText="Next"
        ctaButtonProps={{
          type: 'submit',
        }}
      >
        <Stack spacing={2}>
          <TextField
            label="Product name"
            fullWidth
            size="small"
            {...nameRegister}
            error={isNotNullOrUndefined(get(errors, nameRegister.name))}
            helperText={get(errors, nameRegister.name)?.message}
          />
          <TextField
            label="Description"
            fullWidth
            size="small"
            {...descriptionRegister}
          />

          <RadioGroup>
            <FormControlLabel
              control={<Radio/>}
              disabled
              label={(
                <Typography>
                  Metered product
                </Typography>
              )}
            />

            <FormControlLabel
              value={null}
              control={<Radio/>}
              disabled
              label={(
                <Typography
                  gap={1}
                  display="flex"
                  color={theme => theme.palette.text.disabled}
                >
                  Standard product or service
                  <Chip
                    size="small"
                    label="Coming soon"
                    disabled
                    sx={{
                      fontSize: '10px',
                    }}
                  />
                </Typography>
              )}
            />
          </RadioGroup>
        </Stack>
      </VyDialog>
    </>
  );
};